import { EntityTable } from 'dexie';
import { PageParams } from './types/createOfflineCommonRepository.types';

export const createOfflineCommonRepository = <T, K extends keyof T>(
  getDatabaseTable: () => EntityTable<T, K>,
  searchFields?: (keyof T)[],
) => ({
  save: async (item: T) => {
    const databaseTable = getDatabaseTable();
    await databaseTable.add(item);
    return item;
  },
  saveAll: async (items: T[]) => {
    const databaseTable = getDatabaseTable();
    await databaseTable.bulkAdd(items);
    return items;
  },
  removeAll: async () => {
    const databaseTable = getDatabaseTable();
    await databaseTable.clear();
  },
  getFirst: async () => {
    const databaseTable = getDatabaseTable();
    return databaseTable.toCollection().first();
  },
  getById: async (id: string) => {
    const databaseTable = getDatabaseTable();
    return databaseTable.get({ id });
  },
  getByIdOrFail: async (id: string) => {
    const databaseTable = getDatabaseTable();
    const item = await databaseTable.get({ id });
    if (!item) {
      throw new Error('item not found');
    }
    return item;
  },
  async getExists(id: string) {
    const databaseTable = getDatabaseTable();
    const item = await databaseTable.get({ id });
    return !!item;
  },
  findByIds: async (ids: string[]) => {
    const databaseTable = getDatabaseTable();
    return databaseTable
      .where('id')
      .anyOf(...ids)
      .toArray();
  },
  getExistentIds: async (ids: string[]) => {
    const databaseTable = getDatabaseTable();
    return databaseTable
      .where('id')
      .anyOf(...ids)
      .primaryKeys();
  },
  findAll: async () => {
    const databaseTable = getDatabaseTable();
    return databaseTable.toArray();
  },
  page: async ({ limit, skip, query, orderBy }: PageParams) => {
    const databaseTable = getDatabaseTable();
    let queryBuilder = orderBy ? databaseTable.orderBy(orderBy) : databaseTable.toCollection();

    if (query && searchFields) {
      searchFields.forEach((field, index) => {
        const fieldQuery = databaseTable.where(String(field)).startsWithIgnoreCase(query);
        queryBuilder =
          index === 0 ? fieldQuery : queryBuilder.or(String(field)).startsWithIgnoreCase(query);
      });
    }

    const count = await queryBuilder.count();

    if (skip) {
      queryBuilder.offset(skip);
    }

    if (limit) {
      queryBuilder.limit(limit);
    }

    const data = await queryBuilder.toArray();

    return {
      count,
      data,
    };
  },
});
